import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert } from 'antd';

import type { QuickInferenceDetail } from '../../../helpers/api/quick-inference/quick-inference';
import { useFilterOptionsOptional } from '../../../contexts/FilterOptionsContext';
import { FrameEditorPanel } from './FrameEditorPanel';
import { QuickInferenceFrameThumb } from './QuickInferenceFrameThumb';
import { useCategoryLabels } from './useCategoryLabels';
import {
  EMPTY_PLAYGROUND_FILTER,
  buildFrameEntries,
  buildPlaygroundFilterCatalog,
  displayedFrameViews,
  emptyFrameEntry,
  filterFrameNumbers,
  findFirstFrameWithInferenceOutput,
  findFrameEntryForNumber,
  formatViewLabel,
  frameHasInferenceOutput,
  frameIndexForNumber,
  isPlaygroundFilterActive,
  resolveTotalFrameCount,
  type PlaygroundViewerFilter,
} from './quickInferenceUtils';

interface Props {
  detail: QuickInferenceDetail;
  onDetailRefresh: () => void | Promise<void>;
}

export function QuickInferenceViewerCanvas({ detail, onDetailRefresh }: Props) {
  const [frameIndex, setFrameIndex] = useState(0);
  const [thumbnailRefreshKey, setThumbnailRefreshKey] = useState(0);
  const [framesPanelOpen, setFramesPanelOpen] = useState(false);
  const [viewerFilter, setViewerFilter] = useState<PlaygroundViewerFilter>(EMPTY_PLAYGROUND_FILTER);
  const [hiddenCategoryIds, setHiddenCategoryIds] = useState<number[]>([]);

  const { labelForCategoryId } = useCategoryLabels();
  const filterOptions = useFilterOptionsOptional();

  const frameEntries = useMemo(() => buildFrameEntries(detail), [detail]);
  const totalFrames = useMemo(() => resolveTotalFrameCount(detail), [detail]);

  const allFrameNumbers = useMemo(() => {
    if (totalFrames > 0) {
      return Array.from({ length: totalFrames }, (_, i) => i);
    }
    return [];
  }, [totalFrames]);

  const viewDisplayName = useCallback(
    (code: string) => {
      const def = filterOptions?.views?.find((v) => v.view_code === code);
      return def?.display_name || formatViewLabel(code);
    },
    [filterOptions?.views]
  );

  const filterCatalog = useMemo(
    () =>
      buildPlaygroundFilterCatalog(
        frameEntries,
        allFrameNumbers,
        labelForCategoryId,
        viewDisplayName
      ),
    [frameEntries, allFrameNumbers, labelForCategoryId, viewDisplayName]
  );

  const frameNumbers = useMemo(
    () => filterFrameNumbers(allFrameNumbers, frameEntries, viewerFilter),
    [allFrameNumbers, frameEntries, viewerFilter]
  );

  useEffect(() => {
    setFrameIndex(0);
    setFramesPanelOpen(false);
    setViewerFilter(EMPTY_PLAYGROUND_FILTER);
    setHiddenCategoryIds([]);
  }, [detail.id]);

  useEffect(() => {
    if (detail.status !== 'success' || frameNumbers.length === 0) return;
    const first = findFirstFrameWithInferenceOutput(frameEntries);
    const targetIndex = frameIndexForNumber(frameNumbers, first);
    setFrameIndex((current) => {
      const currentNum = frameNumbers[current] ?? 0;
      const currentEntry =
        findFrameEntryForNumber(frameEntries, currentNum) ?? emptyFrameEntry(currentNum);
      if (frameHasInferenceOutput(currentEntry.frame)) return current;
      return targetIndex;
    });
  }, [detail.status, detail.updated_at, frameEntries, frameNumbers]);

  useEffect(() => {
    if (frameIndex >= frameNumbers.length && frameNumbers.length > 0) {
      setFrameIndex(frameNumbers.length - 1);
    }
  }, [frameIndex, frameNumbers.length]);

  // When filters change and current frame no longer matches, jump to first match.
  useEffect(() => {
    if (!isPlaygroundFilterActive(viewerFilter)) return;
    if (frameNumbers.length === 0) return;
    const currentNum = frameNumbers[frameIndex];
    if (currentNum == null) {
      setFrameIndex(0);
    }
  }, [viewerFilter, frameNumbers, frameIndex]);

  const currentFrameNumber = frameNumbers[frameIndex] ?? allFrameNumbers[0] ?? 0;
  const currentEntry =
    findFrameEntryForNumber(frameEntries, currentFrameNumber) ??
    emptyFrameEntry(currentFrameNumber);
  const canEdit = detail.status === 'success';
  const globalOneBased = frameNumbers.length > 0 ? frameIndex + 1 : 0;

  const goToFrame = useCallback(
    (oneBased: number) => {
      const idx = oneBased - 1;
      if (idx >= 0 && idx < frameNumbers.length) {
        setFrameIndex(idx);
      }
    },
    [frameNumbers.length]
  );

  const handleSaved = useCallback(async () => {
    setThumbnailRefreshKey((k) => k + 1);
    await onDetailRefresh();
  }, [onDetailRefresh]);

  const toggleCategoryVisibility = useCallback((categoryId: number) => {
    setHiddenCategoryIds((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        goToFrame(globalOneBased - 1);
      } else if (e.key === 'ArrowRight') {
        goToFrame(globalOneBased + 1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [globalOneBased, goToFrame]);

  if (detail.status === 'pending') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 rounded-lg border border-gray-200 bg-white p-10 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="relative h-12 w-12">
          <div className="absolute inset-0 animate-spin rounded-full border-[3px] border-blue-100 border-t-blue-500" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
            Running inference on this file…
          </p>
          <p className="mt-1 text-xs text-gray-400">Results will appear automatically</p>
        </div>
      </div>
    );
  }

  if (detail.status === 'fail') {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <Alert type="error" showIcon message={detail.error_message || 'Inference failed'} />
      </div>
    );
  }

  if (detail.status !== 'success' || allFrameNumbers.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
      </div>
    );
  }

  if (frameNumbers.length === 0) {
    return (
      <div className="h-full min-h-[min(70vh,640px)] lg:min-h-0">
        <FrameEditorPanel
          key={`${detail.id}-empty-filter`}
          entry={emptyFrameEntry(0)}
          detail={detail}
          editable={false}
          onSaved={handleSaved}
          onCalibrated={onDetailRefresh}
          totalFrameCount={0}
          globalCurrentFrameIndex={0}
          onGlobalFrameIndexChange={goToFrame}
          showFramesToggle={allFrameNumbers.length > 1}
          framesPanelOpen={false}
          viewerFilter={viewerFilter}
          onViewerFilterChange={setViewerFilter}
          filterViewOptions={filterCatalog.views}
          filterStructureOptions={filterCatalog.structures}
          matchedFrameCount={0}
          totalUnfilteredFrameCount={allFrameNumbers.length}
          emptyFilterMessage
          onClearFilter={() => setViewerFilter(EMPTY_PLAYGROUND_FILTER)}
        />
      </div>
    );
  }

  const frameStrip =
    frameNumbers.length > 0 ? (
      <div className="px-2 py-2">
        <div className="flex gap-1.5 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'thin' }}>
          {frameNumbers.map((num, idx) => {
            const entry =
              findFrameEntryForNumber(frameEntries, num) ?? emptyFrameEntry(num);
            const views = displayedFrameViews(entry.frame);
            const pending = entry.frame.pending_user_views || [];
            return (
              <QuickInferenceFrameThumb
                key={num}
                inferenceId={detail.id}
                frameNumber={num}
                views={views}
                pendingCount={pending.length}
                segmentations={entry.frame.segmentations || []}
                selected={idx === frameIndex}
                refreshKey={thumbnailRefreshKey}
                onClick={() => {
                  setFrameIndex(idx);
                  setFramesPanelOpen(false);
                }}
              />
            );
          })}
        </div>
      </div>
    ) : null;

  return (
    <div className="h-full min-h-[min(70vh,640px)] lg:min-h-0">
      <FrameEditorPanel
        key={`${detail.id}-${currentFrameNumber}-${detail.updated_at}`}
        entry={currentEntry}
        detail={detail}
        editable={canEdit}
        onSaved={handleSaved}
        onCalibrated={onDetailRefresh}
        totalFrameCount={frameNumbers.length}
        globalCurrentFrameIndex={globalOneBased}
        onGlobalFrameIndexChange={goToFrame}
        frameStrip={frameStrip}
        framesPanelOpen={framesPanelOpen}
        onFramesPanelToggle={() => setFramesPanelOpen((o) => !o)}
        showFramesToggle={allFrameNumbers.length > 1}
        viewerFilter={viewerFilter}
        onViewerFilterChange={setViewerFilter}
        filterViewOptions={filterCatalog.views}
        filterStructureOptions={filterCatalog.structures}
        matchedFrameCount={frameNumbers.length}
        totalUnfilteredFrameCount={allFrameNumbers.length}
        hiddenCategoryIds={hiddenCategoryIds}
        onToggleCategoryVisibility={toggleCategoryVisibility}
      />
    </div>
  );
}
